import { types as sdkTypes } from '../util/sdkLoader';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

const isDev = process.env.NODE_ENV === 'development';

/**
 * Extracts the geographic location (origin) from a Google Maps Place object
 * using the new Google Places API structure, and converts it into an SDKLatLng object.
 *
 * @param {google.maps.places.Place} place - An instance of the Google Maps Place class.
 * @returns {SDKLatLng|null} An SDKLatLng object representing the latitude and longitude
 *                           of the place's location.
 *                           Returns null if the place or its location is invalid.
 */
const placeOrigin = place => {
  if (place && place.location) {
    return new SDKLatLng(place.location.lat(), place.location.lng());
  }
  return null;
};

/**
 * Extracts the viewport bounds from a Google Maps Place object using the new Places API,
 * and converts them into an SDKLatLngBounds object.
 *
 * @param {google.maps.places.Place} place - An instance of the Google Maps Place class.
 * @returns {SDKLatLngBounds|null} An SDKLatLngBounds object representing the northeast and
 *                                 southwest corners of the place's viewport.
 *                                 Returns null if the place or its viewport is invalid.
 */
const placeBounds = place => {
  if (place && place.viewport) {
    const ne = place.viewport.getNorthEast();
    const sw = place.viewport.getSouthWest();
    return new SDKLatLngBounds(
      new SDKLatLng(ne.lat(), ne.lng()),
      new SDKLatLng(sw.lat(), sw.lng())
    );
  }
  return null;
};

/**
 * Fetches detailed information about a specific place using the new Google Maps Places API.
 *
 * @param {string} placeId - ID for a place received from the
 * autocomplete service
 * @returns {Promise<Object|undefined>} A promise that resolves to an object containing:
 *   - `adress` (string): The formatted address of the place.
 *   - `origin` (object): The geographic origin of the place (calculated using `placeOrigin`).
 *   - `bounds` (object): The viewport bounds of the place (calculated using `placeBounds`).
 */
/**
 * Extract the postal code string from a Google Maps addressComponents array.
 * @param {Array} addressComponents
 * @returns {string|null}
 */
const extractZipCode = addressComponents => {
  if (!addressComponents) return null;
  const postalComponent = addressComponents.find(c => c.types?.includes('postal_code'));
  return postalComponent ? postalComponent.shortText || postalComponent.longText || null : null;
};

/**
 * Extract a city / neighborhood label from addressComponents.
 * Priority: sublocality_level_1 → neighborhood → locality → admin_area_level_2
 */
const extractLocality = addressComponents => {
  if (!Array.isArray(addressComponents)) return null;
  const priority = [
    'sublocality_level_1',
    'neighborhood',
    'locality',
    'administrative_area_level_2',
  ];
  for (const type of priority) {
    const comp = addressComponents.find(c => c.types?.includes(type));
    if (comp) return comp.longText || comp.shortText || null;
  }
  return null;
};

/**
 * IATA code lookup — three layers:
 * 1. Google Place ID (most reliable, language-independent)
 * 2. City/locality name → IATA (works even when display name is in another language)
 * 3. Keyword substring match on display name
 */
const IATA_BY_PLACE_ID = {
  // Florida
  'ChIJEUSBCbq32YgRIFM2ZYHS_3E': 'MIA',
  'ChIJoTbHa9y22YgR4vFKyHIIDGA': 'FLL',
  'ChIJud2QwRCB2YgR7JFNMX_NQWA': 'PBI',
  'ChIJtZ7GHD-N54gR3sHd0FLmUhc': 'MCO',
  'ChIJR6KFqBpbwokRGOa_KMfxuUs': 'TPA',
  'ChIJ6S5z4EVL2YgRxnVxF30YIDA': 'OPF',
  'ChIJPWRxnq9v2YgRZ3wCbf_pL4s': 'FXE',
  'ChIJZ3VPi6Rv2YgRQ8p5Lkb4AYM': 'BCT',
  // Texas
  'ChIJYWKCXq7AQIYR4HRhAHLOHHQ': 'IAH',
  'ChIJkSyBf2pYQIYRS0sGcGRUPAU': 'HOU',
  'ChIJFWcKzOJbToYRnMloxJHLnEA': 'DFW',
  'ChIJAZCMRGOYToYRDDq3LkF1d4U': 'DAL',
  // New York
  'ChIJbVFXPl9QwokRKEWnUuTcWLI': 'JFK',
  'ChIJK3vOQyNawokRXRZPrPT6aJE': 'LGA',
  'ChIJhbcaVT9lwokRHxTkh2AQT_8': 'EWR',
  // California
  'ChIJ-Tg_KZRLwoARqHpWZkTyBME': 'LAX',
  'ChIJS0rEAVZHmoARNMnBJ3Vn3VY': 'SFO',
  'ChIJPQPfwrFUkFQRpBLExSSiBds': 'SAN',
};

// City/locality → primary IATA code (used when place name is in another language)
const IATA_BY_CITY = {
  'miami': 'MIA',
  'doral': 'MIA',         // MIA's address is in Doral
  'fort lauderdale': 'FLL',
  'west palm beach': 'PBI',
  'palm beach': 'PBI',
  'orlando': 'MCO',
  'tampa': 'TPA',
  'houston': 'IAH',
  'dallas': 'DFW',
  'new york': 'JFK',
  'queens': 'JFK',
  'flushing': 'LGA',
  'east elmhurst': 'LGA',
  'newark': 'EWR',
  'los angeles': 'LAX',
  'san francisco': 'SFO',
  'san diego': 'SAN',
  'chicago': 'ORD',
  'phoenix': 'PHX',
  'denver': 'DEN',
  'seattle': 'SEA',
  'boston': 'BOS',
  'atlanta': 'ATL',
  'washington': 'DCA',
  'las vegas': 'LAS',
  'minneapolis': 'MSP',
  'detroit': 'DTW',
};

// Keyword substring match on display name (any language)
const IATA_KEYWORDS = [
  { code: 'MIA', words: ['miami international', 'internacional de miami', 'miami intl'] },
  { code: 'FLL', words: ['fort lauderdale', 'fort lauderdale-hollywood', 'lauderdale'] },
  { code: 'PBI', words: ['palm beach international', 'west palm beach'] },
  { code: 'MCO', words: ['orlando international', 'orlando intl', 'internacional de orlando'] },
  { code: 'TPA', words: ['tampa international', 'internacional de tampa'] },
  { code: 'JFK', words: ['john f. kennedy', 'jfk', 'kennedy international'] },
  { code: 'LGA', words: ['laguardia', 'la guardia'] },
  { code: 'EWR', words: ['newark liberty', 'newark international'] },
  { code: 'LAX', words: ['los angeles international', 'internacional de los ángeles', 'los ángeles international'] },
  { code: 'SFO', words: ['san francisco international', 'internacional de san francisco'] },
  { code: 'ORD', words: ["o'hare", 'ohare', 'chicago international'] },
  { code: 'ATL', words: ['hartsfield', 'atlanta international'] },
  { code: 'DFW', words: ['dallas/fort worth', 'dallas fort worth'] },
  { code: 'IAH', words: ['george bush intercontinental', 'houston intercontinental'] },
  { code: 'DEN', words: ['denver international', 'internacional de denver'] },
  { code: 'SEA', words: ['seattle-tacoma', 'seatac', 'sea-tac'] },
];

const lookupIata = (displayName, city) => {
  // 1. City lookup (language-independent, most useful for translated names)
  if (city) {
    const code = IATA_BY_CITY[city.toLowerCase()];
    if (code) return code;
  }
  // 2. Keyword match on display name
  if (displayName) {
    const lower = displayName.toLowerCase();
    for (const { code, words } of IATA_KEYWORDS) {
      if (words.some(w => lower.includes(w))) return code;
    }
  }
  return null;
};

const computeMaskedName = (displayName, types, addressComponents, placeId) => {
  const isAirport = Array.isArray(types) && types.includes('airport');

  if (isAirport) {
    // 1. Place ID lookup (language-independent, most reliable)
    if (placeId && IATA_BY_PLACE_ID[placeId]) return IATA_BY_PLACE_ID[placeId];
    // 2. City/keyword lookup
    const city = extractLocality(addressComponents);
    const iata = lookupIata(displayName, city);
    if (iata) return iata;
    // 3. Parse "… (XXX)" pattern
    const match = displayName && displayName.match(/\b([A-Z]{3})\b/);
    if (match) return match[1];
    // 4. Last resort
    return city ? `${city} Airport` : 'Airport';
  }

  // Non-airport: neighborhood → city
  return extractLocality(addressComponents) || displayName || null;
};

export const getPlaceDetails = async placeId => {
  try {
    const place = await new window.google.maps.places.Place({ id: placeId });
    const fields = ['addressComponents', 'displayName', 'formattedAddress', 'viewport', 'id', 'location', 'types'];

    await place.fetchFields({ fields: fields });

    const displayName = place.displayName || null;
    const types = place.types || [];
    const addressComponents = place.addressComponents || [];

    return {
      address: place.formattedAddress,
      origin: placeOrigin(place),
      bounds: placeBounds(place),
      types,
      zipCode: extractZipCode(addressComponents),
      placeName: displayName,
      maskedPlaceName: computeMaskedName(displayName, types, addressComponents, placeId),
    };
  } catch (error) {
    if (isDev) {
      console.error(`Could not get details for place id "${placeId}": `, error);
    }
    return error;
  }
};

/**
 * Fetches autocomplete predictions using the new Google Places API.
 *
 * @param {string} search - Place name or address to search
 * @param {object} sessionToken - Token to tie different autocomplete character searches together
 * with getPlaceDetails call
 * @param {object} searchConfigurations - Defines the search configurations that can be used with
 * the autocomplete service. Used to restrict search to specific region (or regions).
 *
 * @returns {Promise<object>} - An object containing the original search query and predictions array:
 *   - `search` (string): The search query.
 *   - `predictions` (array): An array of prediction objects returned from the Google Places API.
 */
export const getPlacePredictions = async (search, sessionToken, searchConfigurations) => {
  try {
    const sessionTokenMaybe = sessionToken ? { sessionToken } : {};
    const request = {
      input: search,
      ...searchConfigurations,
      ...sessionTokenMaybe,
    };

    const {
      suggestions,
    } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    return {
      search,
      predictions: suggestions || [],
    };
  } catch (error) {
    if (isDev) {
      console.error(
        `Could not get autocomplete suggestions using search query "${search}": `,
        error
      );
    }
    return error;
  }
};

/**
 * Deprecation: use function from src/util/maps.js
 * Cut some precision from bounds coordinates to tackle subtle map movements
 * when map is moved manually
 *
 * @param {LatLngBounds} sdkBounds - bounds to be changed to fixed precision
 * @param {Number} fixedPrecision - integer to be used on tofixed() change.
 *
 * @return {SDKLatLngBounds} - bounds cut to given fixed precision
 */
export const sdkBoundsToFixedCoordinates = (sdkBounds, fixedPrecision) => {
  const fixed = n => Number.parseFloat(n.toFixed(fixedPrecision));
  const ne = new SDKLatLng(fixed(sdkBounds.ne.lat), fixed(sdkBounds.ne.lng));
  const sw = new SDKLatLng(fixed(sdkBounds.sw.lat), fixed(sdkBounds.sw.lng));

  return new SDKLatLngBounds(ne, sw);
};

/**
 * Deprecation: use function from src/util/maps.js
 * Check if given bounds object have the same coordinates
 *
 * @param {LatLngBounds} sdkBounds1 - bounds #1 to be compared
 * @param {LatLngBounds} sdkBounds2 - bounds #2 to be compared
 *
 * @return {boolean} - true if bounds are the same
 */
export const hasSameSDKBounds = (sdkBounds1, sdkBounds2) => {
  if (!(sdkBounds1 instanceof SDKLatLngBounds) || !(sdkBounds2 instanceof SDKLatLngBounds)) {
    return false;
  }
  return (
    sdkBounds1.ne.lat === sdkBounds2.ne.lat &&
    sdkBounds1.ne.lng === sdkBounds2.ne.lng &&
    sdkBounds1.sw.lat === sdkBounds2.sw.lat &&
    sdkBounds1.sw.lng === sdkBounds2.sw.lng
  );
};

/**
 * Calculate a bounding box in the given location
 *
 * @param {latlng} center - center of the bounding box
 * @param {distance} distance - distance in meters from the center to
 * the sides of the bounding box
 *
 * @return {LatLngBounds} bounding box around the given location
 *
 */
export const locationBounds = (latlng, distance) => {
  const bounds = new window.google.maps.Circle({
    center: new window.google.maps.LatLng(latlng.lat, latlng.lng),
    radius: distance,
  }).getBounds();

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  return new SDKLatLngBounds(new SDKLatLng(ne.lat(), ne.lng()), new SDKLatLng(sw.lat(), sw.lng()));
};

/**
 * Helper functions for handling Google OverlayView.
 * Based on https://github.com/tomchentw/react-google-maps/blob/v9.4.5/src/utils/OverlayViewHelper.js
 *
 * @param {element} containerElement - map container
 * @param {object} props - map props
 * @return position offset to allow custom position for the OverlayView
 */
export const getOffsetOverride = (containerElement, props) => {
  const { getPixelPositionOffset } = props;
  //
  // Allows the component to control the visual position of the OverlayView
  // relative to the LatLng pixel position.
  //
  if (typeof getPixelPositionOffset === 'function') {
    return getPixelPositionOffset(containerElement.offsetWidth, containerElement.offsetHeight);
  } else {
    return {};
  }
};

/**
 * Helper functions for handling Google OverlayView
 * Based on https://github.com/tomchentw/react-google-maps/blob/v9.4.5/src/utils/OverlayViewHelper.js
 *
 * @param {MapCanvasProjection} mapCanvasProjection - map projection.
 * @param {object} offset - position offset on map canvas.
 * @param {LatLngBounds} bounds - map bounds.
 *
 * @return styles to render the overlay within the projection.
 */
const getLayoutStylesByBounds = (mapCanvasProjection, offset, bounds) => {
  const ne = mapCanvasProjection.fromLatLngToDivPixel(bounds.getNorthEast());
  const sw = mapCanvasProjection.fromLatLngToDivPixel(bounds.getSouthWest());
  if (ne && sw) {
    return {
      left: `${sw.x + offset.x}px`,
      top: `${ne.y + offset.y}px`,
      width: `${ne.x - sw.x - offset.x}px`,
      height: `${sw.y - ne.y - offset.y}px`,
    };
  }
  return {
    left: `-9999px`,
    top: `-9999px`,
  };
};

/**
 * Helper functions for handling Google OverlayView
 * Based on https://github.com/tomchentw/react-google-maps/blob/v9.4.5/src/utils/OverlayViewHelper.js
 *
 * @param {MapCanvasProjection} mapCanvasProjection - map projection.
 * @param {object} offset - position offset on map canvas.
 * @param {LatLng} position - map position/location.
 *
 * @return  styles to render single coordinate pair within the projection.
 */
const getLayoutStylesByPosition = (mapCanvasProjection, offset, position) => {
  const point = mapCanvasProjection.fromLatLngToDivPixel(position);
  if (point) {
    const { x, y } = point;
    return {
      left: `${x + offset.x}px`,
      top: `${y + offset.y}px`,
    };
  }
  return {
    left: `-9999px`,
    top: `-9999px`,
  };
};

/**
 * Helper functions for handling Google OverlayView
 * Based on https://github.com/tomchentw/react-google-maps/blob/v9.4.5/src/utils/OverlayViewHelper.js
 *
 * @param {MapCanvasProjection} mapCanvasProjection - map projection.
 * @param {object} offset - position offset on map canvas.
 * @param {object} props - map props.
 *
 * @return styles to render an area or a single coordinate pair within the projection.
 */
export const getLayoutStyles = (mapCanvasProjection, offset, props) => {
  const createLatLng = (inst, Type) => {
    return new Type(inst.lat, inst.lng);
  };

  const createLatLngBounds = (inst, Type) => {
    return new Type(
      new window.google.maps.LatLng(inst.ne.lat, inst.ne.lng),
      new window.google.maps.LatLng(inst.sw.lat, inst.sw.lng)
    );
  };

  const ensureOfType = (inst, type, factory) => {
    if (inst instanceof type) {
      return inst;
    } else {
      return factory(inst, type);
    }
  };

  if (props.bounds) {
    const bounds = ensureOfType(props.bounds, window.google.maps.LatLngBounds, createLatLngBounds);
    return getLayoutStylesByBounds(mapCanvasProjection, offset, bounds);
  } else {
    const position = ensureOfType(props.position, window.google.maps.LatLng, createLatLng);
    return getLayoutStylesByPosition(mapCanvasProjection, offset, position);
  }
};
